import type { AdminSupplierSettings } from '@repo/api-contracts';
import { type FormEvent, type ReactElement, useState } from 'react';
import { formatDateTime } from '@/shared/lib';
import { Button, FormField } from '@/shared/ui';
import {
  parseSupplierSettingsForm,
  SUPPLIER_FIELDS,
  type SupplierSettingsFormErrors,
  type SupplierSettingsFormValues,
  supplierSettingsFormFrom,
} from '../model/supplier-settings-form';
import { useUpdateSupplierSettings } from '../model/use-update-supplier-settings';

const LABELS: Record<keyof SupplierSettingsFormValues, string> = {
  businessNumber: '사업자등록번호',
  companyName: '상호',
  representativeName: '대표자명',
  address: '사업장 주소',
  businessType: '업태',
  businessItem: '종목',
  contactName: '담당자명',
  contactEmail: '담당자 이메일',
  contactPhone: '담당자 연락처',
  bank: '은행',
  account: '계좌번호',
  holder: '예금주',
};
export const SupplierSettingsForm = ({
  settings,
}: {
  readonly settings: AdminSupplierSettings;
}): ReactElement => {
  const initial = supplierSettingsFormFrom(settings);
  const [values, setValues] = useState(initial);
  const [baseline, setBaseline] = useState(initial);
  const [baselineUpdatedAt, setBaselineUpdatedAt] = useState(settings.updatedAt);
  const [errors, setErrors] = useState<SupplierSettingsFormErrors>({});
  const { update, isPending, error } = useUpdateSupplierSettings();
  const isDirty = SUPPLIER_FIELDS.some((field) => values[field] !== baseline[field]);
  const displayedValues = isDirty ? values : initial;
  const hasConflict =
    isDirty &&
    (baselineUpdatedAt !== settings.updatedAt ||
      SUPPLIER_FIELDS.some((field) => baseline[field] !== initial[field]));
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = parseSupplierSettingsForm(values);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      return;
    }
    setErrors({});
    if (await update(parsed.input)) {
      setBaseline(values);
      setBaselineUpdatedAt(settings.updatedAt);
    }
  };
  return (
    <form
      aria-label="사업자·입금 계좌 설정"
      noValidate
      onSubmit={(event) => void submit(event)}
      className="flex max-w-xl flex-col gap-4"
    >
      {!settings.complete && (
        <p className="text-sm text-muted-foreground">
          입금 계좌가 비어 있으면 고객 결제 화면에 계좌이체가 나오지 않습니다. 사업자 정보는
          세금계산서 발행에 필요합니다.
        </p>
      )}
      {hasConflict && (
        <div role="alert" className="text-sm text-destructive">
          다른 곳에서 설정이 변경되었습니다. 작성 중인 내용은 유지됩니다. 저장하면 최신 설정을
          덮어씁니다.{' '}
          <button
            type="button"
            className="underline"
            disabled={isPending}
            onClick={() => {
              setValues(initial);
              setBaseline(initial);
              setBaselineUpdatedAt(settings.updatedAt);
              setErrors({});
            }}
          >
            최신 설정 불러오기
          </button>
        </div>
      )}
      {SUPPLIER_FIELDS.map((field) => (
        <FormField
          key={field}
          label={LABELS[field]}
          value={displayedValues[field]}
          error={errors[field]}
          disabled={isPending}
          onChange={(event) => {
            if (!isDirty) {
              setBaseline(initial);
              setBaselineUpdatedAt(settings.updatedAt);
            }
            setValues({ ...displayedValues, [field]: event.target.value });
          }}
        />
      ))}
      <p className="text-sm text-muted-foreground">{`마지막 변경 ${settings.updatedAt ? formatDateTime(settings.updatedAt) : '-'} · ${settings.updatedByName ?? '-'}`}</p>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <Button type="submit" isLoading={isPending} disabled={!isDirty} className="self-start">
        저장
      </Button>
    </form>
  );
};
